/**
 * KPI 概览事件 UI：X 轴数字行、chartRenderEventList、弹窗分组等与舆情 MetricsChart 对齐的辅助函数。
 */
import type {
  ChartConfig,
  EventDataItem,
  EventDataMap,
  TChartRenderEventList,
  TEventDetailsPopGroupedRow,
} from './types';

import {
  eventAxisValueToQueryDate,
  resolveEventTypeDisplayOrderKey,
} from './utils';
import {
  resolveEventRecordDisplayNames,
  resolveEventRecordKeyEventType,
  resolveEventRecordKeyName,
} from './eventRecordDisplayUtils';

export type { TChartRenderEventList, TEventDetailsPopGroupedRow } from './types';

/**
 * 事件唯一键：弹窗内去重、以及 KPI 事件筛选「filter 选中 ↔ 卡片 processedEventData」
 * 跨数据源比对都用这一把 key。
 *
 * 字段优先级：`resolveEventRecordKeyName` / `resolveEventRecordKeyEventType` 与
 * `normalizeRecordForEventWindowsDisplay` 同源，避免仅 `event_title_cn`、无 `event_title_en`
 * 时 `event_title_en ?? name` 因空字符串阻断 cn 兜底导致筛选 key 不一致。
 */
/**
 * 由 chartEventSetting.bubbleEvent 反推事件设置弹窗的 selectValue（按 event_type 分组）。
 * committed=false 且 bubbleEvent 为空 → 默认全选；committed=true → 严格按 bubbleEvent（含全不选）。
 */
export function buildEventSettingSelectValueFromBubbleEvent(
  data: Record<string, unknown[]>,
  bubbleEvent: Record<string, unknown[]> | undefined,
  committed: boolean,
): Record<string, string[]> {
  const shouldUseBubble =
    committed || Object.keys(bubbleEvent ?? {}).length > 0;
  const selectedKeySet = shouldUseBubble
    ? new Set(
      Object.values(bubbleEvent ?? {})
        .flat()
        .map(row => getOnlyOneKey(row as Record<string, unknown>)),
    )
    : null;

  const selected: Record<string, string[]> = {};
  Object.keys(data || {}).forEach(key => {
    const list = (data[key] || []) as Record<string, unknown>[];
    const children = list.map(row => getOnlyOneKey(row));
    selected[key] = selectedKeySet
      ? children.filter(k => selectedKeySet.has(k))
      : [...children];
  });
  return selected;
}

export function getOnlyOneKey(item: Record<string, unknown> | undefined | null): string {
  if (!item) {
    return '$$$';
  }
  const et = resolveEventRecordKeyEventType(item);
  const name = resolveEventRecordKeyName(item);
  const st = item.start_time ?? '';
  const url = item.url ?? '';
  return `${et}$${name}$${st}$${url}`;
}

export function resolveKpiEventUi(cfg: ChartConfig['kpiEventUi']): {
  enabled: boolean;
  settings: boolean;
  xAxisRow: boolean;
} {
  if (cfg === true) {
    return { enabled: true, settings: false, xAxisRow: true };
  }
  if (cfg && typeof cfg === 'object') {
    return {
      enabled: cfg.enabled !== false,
      /** 默认不展示；仅 `settings: true` 时显示事件设置齿轮 */
      settings: cfg.settings === true,
      xAxisRow: cfg.xAxisRow !== false,
    };
  }
  return { enabled: false, settings: false, xAxisRow: false };
}

/** 默认展示优先级：版更 > UA > 玩法 > 商业化 > 实验（与需求一致；可被 chartConfig.eventTypeDisplayOrder 覆盖） */
export const KPI_DEFAULT_EVENT_TYPE_PRIORITY: readonly string[] = [
  '版更',
  'UA',
  '玩法',
  '商业化',
  '实验',
];

/**
 * 根据气泡用事件表构造舆情 EventDetailsPop / X 轴 rich 所需结构。
 */
export const buildChartRenderEventList = (
  bubbleMap: EventDataMap | undefined,
): TChartRenderEventList => {
  const out: TChartRenderEventList = {};
  if (!bubbleMap) {
    return out;
  }
  Object.keys(bubbleMap).forEach(dateKey => {
    const items = bubbleMap[dateKey];
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }
    const event_list: Record<string, EventDataItem[]> = {};
    items.forEach(item => {
      const rawKey = resolveEventTypeDisplayOrderKey(
        item?.event_type ?? item?.eventType,
      );
      const t = rawKey || '其他';
      if (!event_list[t]) {
        event_list[t] = [];
      }
      event_list[t].push(item);
    });
    out[dateKey] = {
      count: items.length,
      event_list,
    };
  });
  return out;
};

/**
 * 事件 Map / chartRenderEventList 多为 YYYYMMDD，折线 xAxis 类目多为 YYYY-MM-DD，需统一再查。
 */
export function expandDateKeyCandidates(raw: string): string[] {
  const v = String(raw ?? '').trim();
  if (!v) {
    return [];
  }
  const out: string[] = [v];
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    out.push(v.replace(/-/g, ''));
  }
  if (/^\d{8}$/.test(v)) {
    out.push(`${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}`);
  }
  return [...new Set(out)];
}

export function resolveChartRenderEntry(
  list: TChartRenderEventList,
  axisValue: string,
): { storageKey: string; entry: TChartRenderEventList[string]; } | undefined {
  for (const k of expandDateKeyCandidates(axisValue)) {
    const entry = list[k];
    if (entry) {
      return { storageKey: k, entry };
    }
  }
  return undefined;
}

function axisLabelCount(
  chartRenderEventList: TChartRenderEventList,
  axisCategoryValue: string,
): number {
  for (const k of expandDateKeyCandidates(axisCategoryValue)) {
    const info = chartRenderEventList[k];
    const v = info?.count ?? 0;
    if (typeof v === 'number' && !Number.isNaN(v)) {
      return v;
    }
  }
  return 0;
}

function isSameAxisDateHighlight(
  highlightKey: string | undefined,
  axisValue: string,
): boolean {
  if (highlightKey === undefined || highlightKey === '') {
    return false;
  }
  const hlSet = new Set(expandDateKeyCandidates(highlightKey));
  return expandDateKeyCandidates(axisValue).some(k => hlSet.has(k));
}

/**
 * 舆情 useXAxisInfo.getEventXAxisAxisLabel 等价实现（X 轴气泡数字 + 高亮）。
 */
export const getDashboardEventXAxisRichAxisLabel = (
  chartRenderEventList: TChartRenderEventList,
  highlightKey?: string,
) => {
  return {
    margin: -6,
    interval: 0,
    rich: {
      eventShow: {
        fontSize: 10,
        color: 'rgb(131, 134, 151)',
        borderRadius: [8, 8, 8, 8],
        padding: [2, 8, 1, 8],
        backgroundColor: '#fff',
        shadowBlur: 2,
        shadowColor: 'rgba(0, 0, 0, 0.3)',
      },
      eventHighlight: {
        fontSize: 10,
        color: '#FFFFFF',
        borderRadius: [8, 8, 8, 8],
        padding: [2, 8, 1, 8],
        backgroundColor: '#326FF0',
        shadowBlur: 2,
        shadowColor: 'rgba(0, 0, 0, 0.3)',
      },
      eventHide: {},
    },
    splitLine: { show: true },
    formatter: (value: string) => {
      const valueNum = axisLabelCount(chartRenderEventList, value);
      const eventNumber =
        valueNum === 0 ? '' : valueNum > 99 ? '99+' : String(valueNum);
      const eventCss = eventNumber ? 'eventShow' : 'eventHide';
      if (isSameAxisDateHighlight(highlightKey, value)) {
        return `{eventHighlight|${eventNumber}}`;
      }
      return `{${eventCss}|${eventNumber}}`;
    },
  };
};

/** 从 rawEventList 聚合成舆情 EventSetting 所需的「大类 → 事件列表」 */
/** rawEventList 内容指纹：用于父组件 memo，避免仅数组引用变化导致子组件反复重置 */
export function buildRawEventListForModalDataKey(
  rawList: Array<Record<string, unknown>> | undefined,
): string {
  if (!Array.isArray(rawList)) {
    return '';
  }
  return JSON.stringify(
    rawList.map(r => {
      const row = (r || {}) as Record<string, unknown>;
      return [
        row.event_type ?? row.eventType,
        row.start_time,
        row.event_title_en,
        row.event_title_cn,
        row.id,
      ];
    }),
  );
}

export const groupRawEventListByTypeForModal = (
  rawList: Array<Record<string, unknown>> | undefined,
): Record<
string,
Array<Record<string, unknown> & { name?: string; name_cn?: string; url?: string; }>
> => {
  const out: Record<
  string,
  Array<Record<string, unknown> & { name?: string; name_cn?: string; url?: string; }>
  > = {};
  if (!Array.isArray(rawList)) {
    return out;
  }
  rawList.forEach(raw => {
    const item = raw as Record<string, unknown>;
    const rawKey = resolveEventTypeDisplayOrderKey(
      item.event_type ?? item.eventType,
    );
    const key = rawKey || '其他';
    const { name, name_cn } = resolveEventRecordDisplayNames(item);
    const row = {
      ...item,
      name,
      name_cn,
      url: typeof item.url === 'string' ? item.url : '',
    };
    if (!out[key]) {
      out[key] = [];
    }
    out[key].push(row);
  });
  return out;
};

export const inferXAxisMatchTimeFormat = (xAxisData: string[]): string => {
  const s = xAxisData.find(v => v !== undefined && v !== null && String(v).trim() !== '');
  if (s && /^\d{8}$/.test(String(s).trim())) {
    return 'YYYYMMDD';
  }
  return 'YYYY-MM-DD';
};

function groupedChartRenderToDetailsPopRows(
  grouped: Record<string, EventDataItem[]>,
): TEventDetailsPopGroupedRow[] {
  const rows: TEventDetailsPopGroupedRow[] = [];
  for (const typeKey of Object.keys(grouped)) {
    const event_record = grouped[typeKey] || [];
    if (event_record.length === 0) continue;
    const first = (event_record[0] || {}) as Record<string, unknown>;
    const etRaw = first.event_type;
    let event_type: TEventDetailsPopGroupedRow['event_type'];
    if (etRaw && typeof etRaw === 'object') {
      const et = etRaw as Record<string, unknown>;
      event_type = {
        id: et.id as string | number | undefined,
        name_en: String(et.name_en ?? et.name ?? typeKey),
        name_cn: String(et.name_cn ?? typeKey),
      };
    } else {
      event_type = {
        id: first.event_type_id as string | number | undefined,
        name_en: String(first.event_type_en ?? etRaw ?? typeKey),
        name_cn: String(first.event_type_cn ?? typeKey),
      };
    }
    rows.push({ event_type, events: event_record.length, event_record });
  }
  return rows;
}

/**
 * 由 chartRenderEventList + xAxisData 构造与 daily useEventDetailsPop 等长 / 等序的 eventData。
 * 每项形如 { events, date, event_list: [{event_type, events, event_record}] }。
 */
export function buildEventDetailsPopAlignedDays(
  xAxisData: string[],
  chartRenderEventList: TChartRenderEventList,
): Array<{
    events: number;
    date: string;
    event_list: TEventDetailsPopGroupedRow[];
  }> {
  return (xAxisData || []).map(axisVal => {
    const date = eventAxisValueToQueryDate(String(axisVal));
    const resolved = resolveChartRenderEntry(
      chartRenderEventList,
      String(axisVal),
    );
    if (!resolved) {
      return { events: 0, date, event_list: [] };
    }
    return {
      events: resolved.entry.count,
      date,
      event_list: groupedChartRenderToDetailsPopRows(resolved.entry.event_list),
    };
  });
}
